import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiError, inr } from "@/lib/api";
import { COST_LABELS } from "@/lib/constants";
import { marginCls } from "./common";

const lines = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);
const LIST_KEYS = ["sightseeing", "activities", "transfers"];
const NEW_DAY = { title: "", description: "", destination: "", sightseeing: "", activities: "", transfers: "", stay: "", overnight: "", meals: "" };
const dayToForm = (d) => ({ ...NEW_DAY, ...d, ...Object.fromEntries(LIST_KEYS.map((k) => [k, (d[k] || []).join("\n")])) });
const dayFromForm = (d, i) => ({
  day: i + 1, title: d.title, description: d.description, destination: d.destination.trim(), stay: d.stay.trim(),
  overnight: d.overnight.trim(), meals: d.meals.trim(), ...Object.fromEntries(LIST_KEYS.map((k) => [k, lines(d[k])])),
});

const Pricing = ({ f, setF }) => {
  const [markup, setMarkup] = useState("");
  const cost = Object.keys(COST_LABELS).reduce((a, k) => a + (Number(f.cost[k]) || 0), 0);
  const sell = Number(f.selling_price) || 0;
  const profit = sell - cost;
  const margin = sell ? (profit / sell) * 100 : 0;
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">Per-person cost components (twin sharing).</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(COST_LABELS).map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l} (₹)</Label>
            <Input type="number" min={0} step="any" value={f.cost[k]} onChange={(e) => setF((s) => ({ ...s, cost: { ...s.cost, [k]: e.target.value } }))} data-testid={`package-cost-${k}-input`} /></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-end">
        <div className="space-y-1.5"><Label>Selling price per person (₹)</Label>
          <Input type="number" min={0} step="any" value={f.selling_price} onChange={(e) => setF((s) => ({ ...s, selling_price: e.target.value }))} data-testid="package-selling-price-input" /></div>
        <div className="flex gap-2">
          <Input type="number" placeholder="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="package-markup-input" />
          <Button type="button" variant="outline" onClick={() => setF((s) => ({ ...s, selling_price: Math.round(cost * (1 + (Number(markup) || 0) / 100)) }))} data-testid="package-apply-markup-button">Apply</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl bg-mist p-4" data-testid="package-margin-summary">
        <div><p className="text-xs text-slate-500">Total cost</p><p className="font-mono font-semibold">{inr(cost)}</p></div>
        <div><p className="text-xs text-slate-500">Selling</p><p className="font-mono font-semibold text-ocean">{inr(sell)}</p></div>
        <div><p className="text-xs text-slate-500">Profit</p><p className={`font-mono font-semibold ${marginCls(margin)}`}>{inr(profit)}</p></div>
        <div><p className="text-xs text-slate-500">Margin</p><p className={`font-mono font-semibold ${marginCls(margin)}`} data-testid="package-margin-value">{margin.toFixed(1)}%</p></div>
      </div>
    </div>
  );
};

const DayFields = ({ d, i, update }) => {
  const inp = (k, ph) => <Input value={d[k]} onChange={(e) => update(i, k, e.target.value)} placeholder={ph} data-testid={`itinerary-${k}-${i}`} />;
  const area = (k, ph) => <Textarea rows={3} value={d[k]} onChange={(e) => update(i, k, e.target.value)} placeholder={ph} data-testid={`itinerary-${k}-${i}`} />;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <div className="sm:col-span-2">{inp("destination", "Destination, e.g. Havelock Island (Swaraj Dweep)")}</div>
      {area("sightseeing", "Sightseeing (one per line)")}{area("activities", "Activities (one per line)")}
      <div className="sm:col-span-2">{area("transfers", "Transfers (one per line)")}</div>
      {inp("stay", "Hotel type, e.g. Premium 4-star beach resort")}{inp("overnight", "Overnight at (blank on departure day)")}
      <div className="sm:col-span-2">{inp("meals", "Meals, e.g. Breakfast")}</div>
    </div>
  );
};

const Itinerary = ({ f, setF }) => {
  const update = (i, k, v) => setF((s) => ({ ...s, itinerary: s.itinerary.map((d, j) => (j === i ? { ...d, [k]: v } : d)) }));
  const renumber = (list) => list.map((d, i) => ({ ...d, day: i + 1 }));
  return (
    <div className="space-y-4">
      {f.itinerary.map((d, i) => (
        <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-2">
          <div className="flex items-center justify-between"><p className="font-mono text-xs font-semibold text-ocean">DAY {i + 1}</p>
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => setF((s) => ({ ...s, itinerary: renumber(s.itinerary.filter((_, j) => j !== i)) }))} data-testid={`itinerary-remove-${i}`}><Trash2 className="h-4 w-4" /></Button></div>
          <Input value={d.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="Title" data-testid={`itinerary-title-${i}`} />
          <Textarea rows={3} value={d.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Description" data-testid={`itinerary-desc-${i}`} />
          <DayFields d={d} i={i} update={update} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => setF((s) => ({ ...s, itinerary: renumber([...s.itinerary, { ...NEW_DAY }]) }))} data-testid="itinerary-add-day-button"><Plus className="h-4 w-4 mr-1" />Add day</Button>
    </div>
  );
};

export const PackageEditDialog = ({ pkg, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (pkg) setF({ ...pkg, highlights: pkg.highlights.join("\n"), inclusions: pkg.inclusions.join("\n"), exclusions: pkg.exclusions.join("\n"), itinerary: pkg.itinerary.map(dayToForm) });
  }, [pkg]);

  const save = async () => {
    setSaving(true);
    try {
      const cost = Object.fromEntries(Object.keys(COST_LABELS).map((k) => [k, Number(f.cost[k]) || 0]));
      const { data } = await api.put(`/admin/packages/${pkg.id}`, {
        name: f.name, tagline: f.tagline, image: f.image, selling_price: Number(f.selling_price) || 0, cost,
        highlights: lines(f.highlights), inclusions: lines(f.inclusions), exclusions: lines(f.exclusions),
        itinerary: f.itinerary.filter((d) => d.title.trim()).map(dayFromForm),
      });
      toast.success("Package saved");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const text = (k, label, rows) => (
    <div className="space-y-1.5"><Label>{label}</Label>
      {rows ? <Textarea rows={rows} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`package-${k}-input`} />
        : <Input value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`package-${k}-input`} />}
    </div>
  );

  return (
    <Dialog open={!!pkg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="package-edit-dialog">
        <DialogHeader><DialogTitle>{pkg?.name}</DialogTitle></DialogHeader>
        {f && (
          <Tabs defaultValue="pricing">
            <TabsList className="mb-4"><TabsTrigger value="pricing" data-testid="package-tab-pricing">Pricing</TabsTrigger><TabsTrigger value="details" data-testid="package-tab-details">Details</TabsTrigger><TabsTrigger value="itinerary" data-testid="package-tab-itinerary">Itinerary</TabsTrigger></TabsList>
            <TabsContent value="pricing"><Pricing f={f} setF={setF} /></TabsContent>
            <TabsContent value="details" className="space-y-4">
              {text("name", "Package name")}{text("tagline", "Tagline")}{text("image", "Image URL")}
              {text("highlights", "Highlights / places (one per line)", 3)}{text("inclusions", "Inclusions (one per line)", 5)}{text("exclusions", "Exclusions (one per line)", 5)}
            </TabsContent>
            <TabsContent value="itinerary"><Itinerary f={f} setF={setF} /></TabsContent>
          </Tabs>
        )}
        <Button onClick={save} disabled={saving} className="mt-2 bg-ocean hover:bg-navy h-11" data-testid="package-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save package"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
